/**
 * Vista de MI certificado (privada, local-first): renderiza la plantilla
 * imprimible + acciones (imprimir/guardar PDF, copiar el enlace de verificación
 * pública). La fila llega por el stream `my_certificates` tras la emisión
 * server-side; si aún no sincronizó, `createEntityResolution` evita dejar la
 * pantalla cargando para siempre (not-found / offline con copy correcto).
 */
import { createMemo, Show, type Component } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Copy, Printer } from "lucide-solid";
import { buildCertificateContent } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@/lib/u-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useCertificate } from "../../hooks/use-course-data";
import { CertificateCard } from "./certificate-card";

/** Al imprimir, aísla la plantilla: solo el nodo [data-certificate-print]. */
const PRINT_ISOLATION_CSS = `
@media print {
  body * { visibility: hidden; }
  [data-certificate-print], [data-certificate-print] * { visibility: visible; }
  [data-certificate-print] { position: fixed; inset: 0; margin: auto; border: none; box-shadow: none; }
}
`;

export const CertificateView: Component<{ certId: string }> = (props) => {
  const { t, locale } = useI18n();
  const { db } = usePowerSyncContext();
  const cert = useCertificate(() => props.certId);

  const resolution = createEntityResolution({
    db,
    hasEntity: () => Boolean(cert.data),
    isPending: () => cert.isLoading,
  });

  const content = createMemo(() => {
    const row = cert.data;
    if (!row) {
      return null;
    }
    return buildCertificateContent(row, {
      verifyBaseUrl: globalThis.location.origin,
      locale: locale(),
    });
  });

  const copyVerifyUrl = async () => {
    const url = content()?.verifyUrl;
    if (!url) {
      return;
    }
    await navigator.clipboard.writeText(url);
    showToast(t(msg`Verification link copied`), "success");
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <style>{PRINT_ISOLATION_CSS}</style>
      <Link
        to="/learn"
        class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-accent print:hidden"
      >
        ← {t(msg`My courses`)}
      </Link>

      <Show
        when={resolution() === "ready"}
        fallback={
          <Show
            when={resolution() === "loading"}
            fallback={
              <NotFoundState
                variant={resolution() === "offline" ? "offline" : "not-found"}
                title={t(msg`Certificate not available`)}
                description={t(
                  msg`This certificate doesn't exist on this device yet. If you just earned it, wait for sync to finish.`,
                )}
              />
            }
          >
            <Skeleton class="h-96 w-full rounded-3xl" />
          </Show>
        }
      >
        <Show when={content()}>
          {(c) => (
            <>
              <CertificateCard content={c()} />
              <div class="flex flex-wrap justify-center gap-3 print:hidden">
                <Button
                  data-testid="certificate-print"
                  onClick={() => {
                    globalThis.window.print();
                  }}
                  class="gap-2"
                >
                  <Printer class="h-4 w-4" />
                  {t(msg`Print / save as PDF`)}
                </Button>
                <Button
                  variant="outline"
                  data-testid="certificate-copy-link"
                  onClick={() => {
                    void copyVerifyUrl();
                  }}
                  class="gap-2"
                >
                  <Copy class="h-4 w-4" />
                  {t(msg`Copy verification link`)}
                </Button>
              </div>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
};
